import { enMinutes } from './creneaux.js';
/**
 * La frontière entre l'horloge murale de la boutique et les instants.
 *
 * ⚠️ Ce fichier existe à cause d'un défaut qui ne se voit QUE si le poste de
 * caisse et la boutique sont dans deux fuseaux différents — un développeur à
 * Paris, une boutique à Douala, par exemple. Le créneau était converti en
 * instant avec l'horloge du NAVIGATEUR, puis relu avec celle de la BOUTIQUE :
 * un rendez-vous pris à 9 h ressortait à 8 h, ne recouvrait plus le créneau de
 * 9 h, et l'agenda proposait joyeusement une heure déjà vendue.
 *
 * ⭐ Une seule horloge fait foi, dans les deux sens : celle de la boutique.
 * « Jeudi 14 h » veut dire 14 h sur place, pour tout le monde.
 */
/**
 * Le décalage d'un fuseau à une date donnée — « +01:00 ».
 *
 * ⚠️ Le décalage dépend de la DATE : un fuseau à heure d'été n'a pas la même
 * valeur en janvier et en juillet. On l'interroge donc pour le jour concerné,
 * jamais une fois pour toutes.
 */
export function decalageFuseau(instant, fuseau) {
    const partie = new Intl.DateTimeFormat('en-US', { timeZone: fuseau, timeZoneName: 'longOffset' })
        .formatToParts(instant)
        .find((p) => p.type === 'timeZoneName')?.value;
    // « GMT+1 » ou « GMT » selon les moteurs : on normalise en « +01:00 ».
    const brut = (partie ?? 'GMT+00:00').replace('GMT', '').trim();
    if (!brut)
        return '+00:00';
    const [signe, reste] = [brut[0], brut.slice(1)];
    const [h = '0', m = '0'] = reste.split(':');
    return `${signe}${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
}
/**
 * « 2026-09-14 » + « 14:00 » dans le fuseau de la boutique → instant ISO.
 *
 * ⚠️ Le décalage est mesuré à partir d'un instant APPROCHÉ du même jour, ce qui
 * suffit partout sauf pendant l'heure même d'un changement d'heure — cas qui
 * n'existe pas dans les fuseaux visés (Douala, Bamako, Abidjan n'en ont pas) et
 * qui, ailleurs, décalerait d'une heure un rendez-vous pris cette nuit-là.
 */
export function instantDepuisHeureMurale(jour, heure, fuseau) {
    const approche = new Date(`${jour}T${heure}:00Z`);
    const decalage = decalageFuseau(approche, fuseau);
    return new Date(`${jour}T${heure}:00${decalage}`).toISOString();
}
/** Un instant → minutes depuis minuit, à l'horloge de la BOUTIQUE. */
export function minutesMurales(iso, fuseau) {
    const heure = new Date(iso).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: fuseau,
    });
    return enMinutes(heure);
}
/** Le jour civil d'un instant, vu de la boutique — « 2026-09-14 ». */
export function jourMural(iso, fuseau) {
    // `en-CA` rend directement AAAA-MM-JJ, sans recomposition manuelle.
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: fuseau });
}
/** Le jour civil qu'il est À LA BOUTIQUE — pas sur le poste qui regarde. */
export function aujourdhuiBoutique(fuseau, maintenant = new Date()) {
    return jourMural(maintenant.toISOString(), fuseau);
}
/**
 * Un instant → « 21/10/2026 10:00 », à l'horloge de la BOUTIQUE.
 *
 * ⚠️ À préférer à `dateHeureLisible` partout où la date décrit un fait
 * d'agenda — un rendez-vous, une fermeture. `dateHeureLisible` rend l'heure du
 * POSTE qui regarde : un gérant à Paris lisait « 10:00 » là où sa boutique de
 * Douala entendait 9 h, et relisait donc une fermeture décalée d'une heure sur
 * la grille d'à côté.
 */
export function dateHeureBoutique(iso, fuseau) {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime()))
        return '—';
    return d.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: fuseau,
    });
}
/**
 * La première minute encore RÉSERVABLE d'une journée, à l'horloge de la
 * boutique.
 *
 *   • jour passé   → +∞ : plus rien ne s'y réserve ;
 *   • aujourd'hui  → la minute qu'il est À LA BOUTIQUE ;
 *   • jour à venir → -1 : tout reste ouvert.
 *
 * ⚠️ La référence est la BOUTIQUE, jamais le poste. Consulté depuis Paris, un
 * agenda de Douala comparait le minuit PARISIEN du jour affiché au jour de
 * Douala : minuit du 11 à Paris tombe le 10 à Douala, et la journée du
 * lendemain se prenait pour aujourd'hui — ses créneaux du matin grisés dès le
 * soir, pendant que la journée en cours laissait réserver ses heures passées.
 *
 * ⭐ Une seule règle pour la grille, les modales et le calendrier : si chacun
 * jugeait « passé » à sa manière, l'un proposerait ce que l'autre refuse.
 *
 * @param jour date CIVILE « AAAA-MM-JJ » du jour considéré.
 */
export function plancherDuJour(jour, fuseau, maintenant = new Date()) {
    const aujourdhui = aujourdhuiBoutique(fuseau, maintenant);
    if (jour < aujourdhui)
        return Number.POSITIVE_INFINITY;
    if (jour > aujourdhui)
        return -1;
    return minutesMurales(maintenant.toISOString(), fuseau);
}
//# sourceMappingURL=fuseau.js.map